import * as React from 'react';
import { cn } from '../../../utils';

export type MiniPosterPalette = 1 | 2 | 3 | 4;

export interface MiniPosterProps {
  children?: React.ReactNode;
  /** palette variant 1–4 (red / paper / charm / dark) */
  palette?: MiniPosterPalette;
  /** dashed "yours next" placeholder variant (no tape, no shadow) */
  next?: boolean;
  className?: string;
}

/** Small rotated poster card with a tape strip, for the back-cover wall. */
export function MiniPoster({ children, palette = 1, next = false, className }: MiniPosterProps) {
  return (
    <span
      className={cn('z-mini-poster', next ? 'z-next' : `z-mp${palette}`, className)}
    >
      {children}
    </span>
  );
}

export interface BackCoverPoster {
  text: React.ReactNode;
  /** palette 1–4; when omitted, palettes cycle by index */
  palette?: MiniPosterPalette;
}

export interface BackCoverProps {
  /** condensed h2, e.g. "Finish the issue, paste the poster" */
  title: React.ReactNode;
  /** sub line under the title */
  sub?: React.ReactNode;
  /** earned posters shown on the wall */
  posters: BackCoverPoster[];
  /** lines of the dashed "next" poster (e.g. ["Nº 12", "yours", "next"]) */
  nextPosterLines?: React.ReactNode[];
  /** big CTA label, e.g. "Finish class · claim your poster" */
  ctaLabel: React.ReactNode;
  onCta?: React.MouseEventHandler<HTMLButtonElement>;
  ctaDisabled?: boolean;
  /** leave the CTA out entirely (e.g. read-only journey access) */
  hideCta?: boolean;
  /** teaser line under the CTA (use <b> for the highlighted title) */
  teaser?: React.ReactNode;
  /** accessible name for the poster wall */
  wallAriaLabel?: string;
  className?: string;
}

/**
 * Dark back cover: headline, sub line, poster wall (rotated mini-posters
 * plus a dashed "next" card), hard-shadow CTA button and a next-issue teaser.
 */
export function BackCover({
  title,
  sub,
  posters,
  nextPosterLines,
  ctaLabel,
  onCta,
  ctaDisabled,
  hideCta = false,
  teaser,
  wallAriaLabel,
  className,
}: BackCoverProps) {
  return (
    <footer className={cn('z-backcover', className)}>
      <div className="z-page">
        <h2>{title}</h2>
        {sub != null && <p className="z-sub">{sub}</p>}
        {(posters.length > 0 || (nextPosterLines?.length ?? 0) > 0) && (
          <div className="z-wall" aria-label={wallAriaLabel}>
            {posters.map((poster, i) => (
              <MiniPoster
                key={i}
                palette={poster.palette ?? (((i % 4) + 1) as MiniPosterPalette)}
              >
                {poster.text}
              </MiniPoster>
            ))}
            {nextPosterLines != null && nextPosterLines.length > 0 && (
              <MiniPoster next>
                {nextPosterLines.map((line, i) => (
                  <React.Fragment key={i}>
                    {i > 0 && <br />}
                    {line}
                  </React.Fragment>
                ))}
              </MiniPoster>
            )}
          </div>
        )}
        {!hideCta && (
          <button type="button" className="z-cta" onClick={onCta} disabled={ctaDisabled}>
            {ctaLabel}
          </button>
        )}
        {teaser != null && <p className="z-next-teaser">{teaser}</p>}
      </div>
    </footer>
  );
}
